"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@batch-and-bowl/ui/button";

interface RouteErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Next.js App Router route-level error boundary — catches render errors thrown
 * anywhere below the root layout. Unlike global-error.tsx it renders *inside*
 * the root layout, so it inherits the theme, fonts and global CSS and must not
 * emit its own <html>/<body>.
 *
 * Colours come from the design tokens in app/styles/theme.css (via the Tailwind
 * token utilities), so the fallback follows light/dark like the rest of the app.
 */
export default function RouteError({ error, reset }: RouteErrorProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-muted text-destructive">
        <AlertTriangle aria-hidden="true" className="size-6" />
      </span>
      <h2 className="text-lg text-foreground">
        Something went wrong
      </h2>
      <p className="max-w-prose text-muted-foreground">
        This part of the page failed to load. We&apos;ve been notified — try
        again, and reload the page if it keeps happening.
      </p>
      {error.digest ? (
        <p className="text-xs text-muted-foreground">
          Reference: <code>{error.digest}</code>
        </p>
      ) : null}
      <Button onClick={reset}>Try again</Button>
    </div>
  );
}
